import lodashIsNumber from "lodash/fp/isNumber";
import { DateTime } from "luxon";
import {
  notification_status,
  notification_event_type,
} from "prisma-generated/enums";
import { Fragment, useMemo } from "react";
import { apiRequestContractHandlerPropsInput } from "shared/api/apiRequestContractHandler";
import { getSocietyProfileNotificationsContract } from "shared/api/contracts/society/societyId/profiles/profileId/notifications";
import { OrderTypes } from "shared/api/types/pagination";
import PillarForm from "shared/components/pillar-form/PillarForm";
import PillarTable from "shared/components/pillar-table/PillarTable";
import {
  PaginationType,
  usePillarTableContext,
} from "shared/components/pillar-table/PillarTableContext";
import { useCustomRouter } from "shared/context/CustomRouterContext";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { isoDateTimeToLocal } from "shared/datetime/isoDateTimeToLocal";
import { findRoute } from "shared/helpers/routes/findRoute";
import type { NotificationEventView } from "shared/mappers/database/notification-event/notification-event";
import { useNotificationsContext } from "hub/src/context/NotificationsProvider/NotificationsContext";

export const getNotificationStatus = (notification: NotificationEventView) => {
  return (notification?.recipients ?? [])?.[0]?.status;
};

type NotificationTableItemProps = {
  notification: NotificationEventView;
};

const NotificationTableItem = ({
  notification,
}: NotificationTableItemProps) => {
  const { tableRowResults: notifications } =
    usePillarTableContext<NotificationEventView>();

  const isMessageNotification =
    notification.eventType === notification_event_type.message_notification;
  const isRead =
    getNotificationStatus(notification) === notification_status.read;
  const chatId = notification?.chat?.chatId;

  const latestUnreadChatMessageNotification = useMemo(() => {
    return notifications.find((notification) => {
      const isMessageNotification =
        notification.eventType === notification_event_type.message_notification;
      const isRead =
        getNotificationStatus(notification) === notification_status.read;
      const notificationChatId = notification?.chat?.chatId;
      return isMessageNotification && !isRead && notificationChatId === chatId;
    });
  }, [notifications, chatId]);

  const shouldDisplay =
    !isMessageNotification ||
    notification.notificationEventId ===
      latestUnreadChatMessageNotification?.notificationEventId;

  if (!shouldDisplay) {
    return <Fragment key={notification.notificationEventId} />;
  }

  return (
    <div color="light" className="notifications-table-item">
      <p className="grow text-detailblock-subdued">
        {isoDateTimeToLocal(
          notification?.created?.timestamp || DateTime.now().toISO(),
        )}
      </p>

      <h4 className="grow text-base-semibold-dark">
        {notification?.notificationTitle}
      </h4>

      <p
        className={`text-detailblock-subtle whitespace-pre-line ${isMessageNotification && "line-clamp-2"}`}
      >
        {notification?.notificationBody}
      </p>

      {isMessageNotification && (
        <div className="flex flex-row justify-end items-center">
          <PillarForm.Button title="Open" label="Open" />
        </div>
      )}

      {!isRead && (
        <div className="absolute top-1 right-1 size-1 rounded-full bg-status-terminated pointer-events-none" />
      )}
    </div>
  );
};

export const NotificationsTable = () => {
  const router = useCustomRouter();
  const session = useCustomSession();
  const { notificationsModalControl: control } = useNotificationsContext();

  const chatDetailsRouteConfig = findRoute(session?.society?.routes ?? [], {
    componentName: "chatDetailsPage",
  });

  return (
    <PillarTable<NotificationEventView>
      className="notifications-table"
      pillarTableContext={{
        wrapperClassName: "notifications-table-wrapper",
      }}
      queryKey={[
        "notifications",
        session?.societyId?.toString() ?? "",
        session?.profileId?.toString() ?? "",
      ]}
      dataQuery={async (filters, pagination) => {
        if (!session?.societyId || !session?.profileId) {
          return {
            results: [],
            page: 0,
            pageSize: 0,
            totalResults: 0,
          };
        }

        return apiRequestContractHandlerPropsInput({
          contract: getSocietyProfileNotificationsContract,
          inputs: {
            params: {
              societyId: session.societyId,
              profileId: session.profileId,
            },
          },
          filters: {
            ...filters,
            status: [
              notification_status.sent,
              notification_status.unread,
              notification_status.read,
            ],
          },
          pagination: {
            page: 1,
            orderByKey: "notificationEventId",
            orderType: OrderTypes.desc,
            ...pagination,
            pageSize: 100,
          },
        });
      }}
      pagination={PaginationType.Infinity}
      isTableWithFixedHeight
      emptyTableMessage="No notifications yet"
      handleRowClick={(notification) => {
        const chatDetailsRouteUrl = chatDetailsRouteConfig?.url;
        const chatId = notification.chat?.chatId;

        if (chatDetailsRouteUrl && lodashIsNumber(chatId)) {
          control.setIsOpen(false);
          router.removeFromState("profile");
          router.push(chatDetailsRouteUrl, { chatId });
        }
      }}
    >
      <PillarTable.Filters className="!h-fit"></PillarTable.Filters>

      <PillarTable.Column className="flex" contentClassName="h-full w-full">
        {(notification: NotificationEventView) => {
          return (
            <NotificationTableItem
              key={notification.notificationEventId}
              notification={notification}
            />
          );
        }}
      </PillarTable.Column>
    </PillarTable>
  );
};
